import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { toCents } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Wallet } from "lucide-react";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

export default function OpenCashDialog({ open, onOpenChange, currency }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const mut = useMutation({
    mutationFn: async () => (await api.post("/api/cash/open",
      { opening_cash_cents: toCents(amount), note: note || null }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Caisse ouverte"); qc.invalidateQueries(); onOpenChange(false); setAmount(""); setNote(""); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="open-cash-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Ouvrir la caisse</DialogTitle>
          <DialogDescription className="sr-only">Saisissez le fond de caisse initial pour ouvrir une session.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Fond de caisse initial ({currency})</label>
            <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2000" data-testid="opening-amount-input" className={input} autoFocus />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Note (optionnel)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ouverture matin" data-testid="opening-note-input" className={input} />
          </div>
          <button disabled={mut.isPending || amount === ""} onClick={() => mut.mutate()} data-testid="confirm-open-cash-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Ouvrir la caisse
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
